import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, lastValueFrom } from 'rxjs';
import { Applicant, DataRegistration } from '../../models/applicant.model';
import { environment } from '../../../environments/environment';
import {
  HashInformationValidation,
  InviteOrigin,
  RegistrationOrigin,
} from '../../models/invite.model';
import { InviteJobApplicationStorageService } from '../invite-job-application-storage/invite-job-application-storage.service';
import { InviteEasyApplyStorageService } from '../invite-easy-apply-storage/invite-easy-apply-storage.service';
import { InviteTalentBankStorageServiceService } from '../talent-bank/invite-talent-bank-storage-service.service';
import { RequestServiceService } from '../request/request-service.service';
import { State } from 'ats-ui-lib';

export interface InviteResult {
  statusCode: number;
}

@Injectable({
  providedIn: 'root',
})
export class InviteRequestService extends State<HashInformationValidation> {
  constructor(
    private http: HttpClient,
    private window: Window,
    private talentBankStorage: InviteTalentBankStorageServiceService,
    private jobApplicationStorage: InviteJobApplicationStorageService,
    private easyApplyStorage: InviteEasyApplyStorageService,
    private requestService: RequestServiceService,
  ) {
    super({} as any);
  }

  getParticipantInviteRequest(hashInvite: string): Observable<Applicant> {
    return this.http.get<Applicant>(
      `${environment.applicantApiEndpoint}/invite-request/${hashInvite}`,
    );
  }

  refuseInvite(hashInvite: string): Observable<InviteResult> {
    return this.http.put<InviteResult>(
      `${environment.applicantApiEndpoint}/invite-request/refuse-invite/${hashInvite}`,
      {},
    );
  }

  acceptInvite(hashInvite: string): Observable<InviteResult> {
    return this.http.put<InviteResult>(
      `${environment.applicantApiEndpoint}/invite-request/accept-invite/${hashInvite}`,
      {},
    );
  }

  async getHashInformationValidation(hashInvite: string): Promise<HashInformationValidation> {
    const result = await lastValueFrom(
      this.http.get<HashInformationValidation>(
        `${environment.applicantApiEndpoint}/invite-request/hash-information-validation/${hashInvite}`,
      ),
    );

    if (result) this.setState(result);
    return result;
  }

  saveHashInvite(origin: InviteOrigin | RegistrationOrigin): string | null {
    const urlStr = this.window.location.href;
    if (!urlStr) return null;

    const url = new URL(urlStr);
    const hashInvite = this.sanitizeHash(
      url.searchParams.get('hash-invite') ||
        (origin === RegistrationOrigin.EasyApply ? url.searchParams.get('token') : null),
    );

    if (!hashInvite) return null;

    if (origin === InviteOrigin.TalentBank) {
      this.talentBankStorage.setInvite(hashInvite);
    } else if (origin === InviteOrigin.JobOpportunity) {
      this.jobApplicationStorage.setInvite(hashInvite);
    } else {
      this.easyApplyStorage.setInvite(hashInvite);
    }

    return hashInvite;
  }

  getHashInviteFromURL(): string | null {
    const url = new URL(this.window.location.href);
    return this.sanitizeHash(url.searchParams.get('hash-invite') || url.searchParams.get('token'));
  }

  // API's not authorized
  getDataRegistrationByHash(hashInvite: string, isEasyApply = false): Observable<DataRegistration> {
    const normalizedHash = this.sanitizeHash(hashInvite);
    if (!normalizedHash) return from(Promise.reject('Invalid hash invite'));

    const url = isEasyApply
      ? this.getEasyApplyUrl(normalizedHash)
      : this.getInviteUrl(normalizedHash);

    const promise = this.requestService
      .requestFetch(url, {
        method: 'GET',
        mode: 'cors',
      })
      .then((response) => response.json())
      .then((value: DataRegistration) => value);

    return from(promise);
  }

  private sanitizeHash(hash: string | null): string | null {
    if (!hash) return null;
    return hash.trim().replace(/['"]/g, '');
  }

  private getEasyApplyUrl(token: string): string {
    return `${environment.applicantApiEndpoint}/job-applications/easy-applies/token?token=${encodeURIComponent(token)}`;
  }

  private getInviteUrl(hash: string): string {
    return `${environment.applicantApiEndpoint}/invite-request/${hash}/data-for-registration`;
  }
}
